<template>
  <ModalRoot title="Wszystkie oferty / Konfigurator" :closeByClickingOutside="false" @close="$emit('close')">
    <Loader v-if="!loaded || updating" />
    <Search v-show="loaded" v-model="search" @input="updateAvailableValues" />
    <OfferSelector v-if="loaded && !updating" :offers="offers" />
    <MoreOffersLoader v-if="!updating" :offersToLoad="offersToLoad" @load="loadMoreOffers" />
  </ModalRoot>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import OfferSelector from './OfferSelector'

import Icon from '@/components/Icon'
import Loader from '@/components/Loader'
import ModalRoot from '@/components/ModalRoot'
import MoreOffersLoader from '@/components/offers-list/MoreOffersLoader'
import Search from '@/components/Search'
import { API, SearchConstraintsRequest, HotelAllOffersRequest } from '@/modules/api'

@Component({
  components: {
    ModalRoot,
    Loader,
    Search,
    OfferSelector,
    Icon,
    MoreOffersLoader,
  },
  props: {
    objectId: {
      required: true,
      type: String,
    },
  },
})
export default class OtherTerminsAndPricesModal extends Vue {
  loaded = false
  updating = false
  offers = []
  offersToLoad = 0
  pageSize = 10

  search = {
    airport: [
      { name: 'Katowice', value: 'KTR', enabled: true, selected: false },
      { name: 'Warszawa', value: 'WAW', enabled: true, selected: false },
      { name: 'Gdańsk', value: 'GDN', enabled: false, selected: false },
    ],
    dates: {
      mode: 'start',
      selected: moment.range(moment(), moment().add('years', 2)),
      validDates: moment.range(moment(), moment().add('years', 2)),
    },
    duration: {
      range: '',
      ranges: [ { name: 'CC', value: 'c' } ],
    },
    people: {
      adults: { value: 2, min: 0, max: 9 },
      children: { value: 0, min: 0, max: 6 },
      childrenBirthdays: [],
      checkChildrenBirthday: true,
    },
    room: {
      selected: '',
      options: [ { name: 'AA', value: 'a' } ],
    },
    boarding: {
      selected: '',
      options: [ { name: 'XX', value: 'x' } ],
    },
    pricePerPerson: {
      selected: '',
      options: [ { name: 'GG', value: 'g' } ],
    },
  }

  mounted () {
    this.updateAvailableSearchOptions().then(this.updateAvailableValues)
  }

  updateAvailableValues () {
    this.updating = true
    this.offersToLoad = 0

    Promise
      .all([ this.updateSearchConstraints(), this.updateOffersList() ])
      .then(() => {
        this.loaded = true
        this.updating = false
      })
  }

  // Available search options

  updateAvailableSearchOptions () {
    return new API()
      .getOffersFilter(this.objectId)
      .then(this.updateSearchOptionsFromResponse)
  }

  updateSearchOptionsFromResponse (response) {
    this.search.airport = response.airports
    this.search.room.options = response.roomTypes
    this.search.boarding.options = response.boardStandards
    this.search.pricePerPerson.options = response.priceRanges
    this.search.duration = response.durations
  }

  // Search constraints

  updateSearchConstraints () {
    return new API()
      .getSearchConstraintsForHotelCode(this.objectId, this.getSearchConstraintRequest())
      .then(this.updateSearchConstraintsFromResponse)
  }

  getSearchConstraintRequest () {
    const request = new SearchConstraintsRequest()
    request.DestinationAirports.addAll(this.search.airport.filter(airport => airport.selected).map(airport => airport.value))
    if (this.search.dates.mode == 'date') {
      request.DepartureDateMin = this.search.dates.selected.start
    } else {
      request.DepartureDateMin = this.search.dates.selected.start
      request.ReturnDateMax = this.search.dates.selected.end
    }
    request.AdultsCount = this.search.people.adults.value
    request.ChildrenCount = this.search.people.children.value
    request.ChildrenBirthDates.concat(this.search.people.childrenBirthdays.map(date => date.format('YYYYMMDD')))
    request.Room = this.search.room.selected
    request.BoardStandards = this.search.boarding.selected
    request.Price = this.search.pricePerPerson.selected
    request.DurationInterval = this.search.duration.range

    return request
  }

  updateSearchConstraintsFromResponse (response) {
    this.markAvailabilityOfListEntries(this.search.airport, response.airports)
    this.search.airport = this.search.airport.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.search.dates.validDates = moment.range(response.minDate, response.maxDate)
    this.search.dates.selected = moment.range(
      moment.max(this.search.dates.selected.start, response.minDate),
      moment.min(this.search.dates.selected.end, response.maxDate)
    )

    this.search.people.adults.min = response.adults.min
    this.search.people.adults.max = response.adults.max
    this.search.people.adults.value = Math.max(this.search.people.adults.value, response.adults.min)
    this.markAvailabilityOfRadioElements(this.search.duration.ranges, response.validRanges)
  }

  // Offers

  updateOffersList () {
    return new API()
      .getHotelAllOffers(this.objectId, this.getSearchOffersRequest())
      .then(this.updateOffersFromInitialRequest)
  }

  loadMoreOffers () {
    return new API()
      .getHotelAllOffers(this.objectId, this.getSearchOffersRequest(this.offers.length / this.pageSize))
      .then(this.updateOffersFromSecondaryResponse)
  }

  getSearchOffersRequest (page = 0) {
    const request = new HotelAllOffersRequest()
    request.pageFrom = page
    request.airports.addAll(this.search.airport.filter(airport => airport.selected).map(airport => airport.value))
    if (this.search.dates.mode == 'date') {
      request.Dates = `${this.search.dates.selected.start.format('YYYYMMDD')}:${this.search.dates.validDates.end.format('YYYYMMDD')}`
    } else {
      request.Dates = `${this.search.dates.selected.start.format('YYYYMMDD')}:${this.search.dates.selected.end.format('YYYYMMDD')}`
    }
    request.Adults = this.search.people.adults.value
    request.Children = this.search.people.children.value
    for (let i = 1; i <= this.search.people.children.value; i++) {
      request[`Child${i}`] = this.search.people.childrenBirthdays[i - 1].format('YYYYMMDD')
    }
    request.Room = this.search.room.selected
    request.BoardStandards = this.search.boarding.selected
    request.Price = this.search.pricePerPerson.selected
    request.DurationInterval = this.search.duration.range
    request.SortBy = 6
    request.SortAsc = 1

    return request
  }

  updateOffersFromInitialRequest (response) {
    this.offers = response.offers
    this.offersToLoad = response.offersToLoad
    this.pageSize = response.pageSize
  }

  updateOffersFromSecondaryResponse (response) {
    this.offers = this.offers.concat(response.offers)
    this.offersToLoad = response.offersToLoad
    this.pageSize = response.pageSize
  }

  // Utilities

  markAvailabilityOfListEntries (list, ids) {
    list.forEach(item => {
      item.enabled = ids.some(x => x === item.value)
      if (!item.enabled) item.selected = false
    })
  }

  markAvailabilityOfRadioElements (list, ids) {
    list.forEach(item => {
      item.enabled = ids.some(x => x === item.value)
    })
  }

  selectDateRange (ranges) {
    return ranges.find(range => range == this.search.dates.range) || ranges[0]
  }
}
</script>


// WEBPACK FOOTER //
// src/components/offers-list/OtherTerminsAndPricesModal.vue